/**
 * listkit — shared building blocks for the MUAA admin's list/index pages, in the
 * delivered navy-gold design (the same visual language as the Dashboard). Every
 * piece is brand-token styled with INLINE styles (no AntD-theme dependency, so it
 * renders identically inside unit tests) and purely presentational — each page
 * computes its own numbers and hands them in.
 *
 *   <ListPage
 *     intro={{ title: 'Donations', description: 'Every gift members make…' }}
 *     stats={[{ label: 'Raised', value: '$12.3K', icon: HandHeart, tone: 'gold' }]}
 *   >
 *     <List title={false}> … table … </List>
 *   </ListPage>
 */
import React from 'react';
import { useNavigation } from '@refinedev/core';
import { brand } from './brand';
import type { AdminIcon } from '../components/icons';

/** The Dashboard's white surface card. */
export const surface: React.CSSProperties = {
  background: '#fff',
  border: '1px solid ' + brand.cardLine,
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(16,30,60,.04)',
};

export type Tone = 'gold' | 'jaguar' | 'green' | 'red' | 'neutral';

export const TONES: Record<Tone, { fg: string; chipBg: string; chipBorder: string }> = {
  gold: { fg: brand.gold, chipBg: 'rgba(196,151,49,.12)', chipBorder: 'rgba(196,151,49,.30)' },
  jaguar: { fg: brand.jaguar, chipBg: 'rgba(30,78,140,.10)', chipBorder: 'rgba(30,78,140,.25)' },
  green: { fg: '#1f7a4d', chipBg: '#e3f3ea', chipBorder: 'rgba(31,122,77,.25)' },
  red: { fg: '#a4452f', chipBg: '#f6e4de', chipBorder: 'rgba(164,69,47,.25)' },
  neutral: { fg: brand.sub, chipBg: brand.paper, chipBorder: brand.cardLine },
};

export interface Stat {
  label: string;
  value: React.ReactNode;
  /** Small pill in the top-right (e.g. "+12 this week"). */
  hint?: string;
  icon?: AdminIcon;
  tone?: Tone;
}

/** A responsive deck of Dashboard-style KPI cards. */
export function StatStrip({ stats, columns }: { stats: Stat[]; columns?: number }) {
  const cols = columns ?? Math.min(Math.max(stats.length, 2), 4);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`,
        gap: 14,
        marginBottom: 18,
      }}
    >
      {stats.map((s, i) => {
        const tone = TONES[s.tone ?? 'gold'];
        const Icon = s.icon;
        return (
          <div key={i} style={{ ...surface, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
              {Icon ? (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: tone.chipBg,
                    border: '1px solid ' + tone.chipBorder,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={18} color={tone.fg} />
                </div>
              ) : (
                <span />
              )}
              {s.hint ? (
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 10.5,
                    color: tone.fg,
                    background: tone.chipBg,
                    padding: '3px 8px',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.hint}
                </span>
              ) : null}
            </div>
            <div
              style={{
                fontFamily: brand.fDisplay,
                fontWeight: 800,
                fontSize: 26,
                color: brand.ink,
                marginTop: 12,
                lineHeight: 1.05,
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 600,
                fontSize: 11,
                color: brand.sub,
                marginTop: 5,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface IntroProps {
  /** Optional — the AdminLayout header already shows the page title; use this for a richer in-page title. */
  title?: React.ReactNode;
  eyebrow?: string;
  description: React.ReactNode;
  /** Right-aligned slot (e.g. a Refine <CreateButton/>). */
  actions?: React.ReactNode;
}

/** A page lede: optional gold eyebrow + title and a one-line "what this page does". */
export function PageIntro({ title, eyebrow, description, actions }: IntroProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 18,
      }}
    >
      <div style={{ maxWidth: 760 }}>
        {eyebrow ? (
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: brand.gold,
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
        ) : null}
        {title ? (
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 20, color: brand.ink, marginBottom: 4 }}>
            {title}
          </div>
        ) : null}
        <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, lineHeight: 1.55 }}>
          {description}
        </div>
      </div>
      {actions ? <div style={{ flexShrink: 0 }}>{actions}</div> : null}
    </div>
  );
}

/** Card title row (matches the Dashboard's CardHead). */
export function CardHead({
  title,
  sub,
  action,
  onAction,
}: {
  title: React.ReactNode;
  sub?: string;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 15, color: brand.ink }}>{title}</div>
        {sub ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub, marginTop: 2 }}>
            {sub}
          </div>
        ) : null}
      </div>
      {action ? (
        <span
          onClick={onAction}
          style={{
            fontFamily: brand.fSans,
            fontWeight: 600,
            fontSize: 12,
            color: brand.gold,
            cursor: onAction ? 'pointer' : 'default',
          }}
        >
          {action}
        </span>
      ) : null}
    </div>
  );
}

/** White surface card with an optional head; the standard content container. */
export function SectionCard({
  title,
  sub,
  action,
  onAction,
  children,
  style,
  bodyStyle,
}: {
  title?: React.ReactNode;
  sub?: string;
  action?: string;
  onAction?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
}) {
  return (
    <div style={{ ...surface, padding: 18, ...style }}>
      {title ? <CardHead title={title} sub={sub} action={action} onAction={onAction} /> : null}
      <div style={{ marginTop: title ? 14 : 0, ...bodyStyle }}>{children}</div>
    </div>
  );
}

/** Gold progress bar (campaigns, leaderboard, goals). */
export function ProgressBar({ value, max, height = 9 }: { value: number; max: number; height?: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div
      style={{
        height,
        borderRadius: 999,
        background: brand.paper,
        overflow: 'hidden',
        border: '1px solid ' + brand.cardLine,
      }}
    >
      <div style={{ width: pct + '%', height: '100%', background: brand.goldGrad, borderRadius: 999 }} />
    </div>
  );
}

/** Generic rounded pill, toned. */
export function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: Tone }) {
  const t = TONES[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: brand.fSans,
        fontWeight: 700,
        fontSize: 11,
        color: t.fg,
        background: t.chipBg,
        padding: '3px 11px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** Initials avatar disc (jaguar field, gold ring) — matches the Dashboard/columns avatar. */
export function AvatarDisc({ initials, size = 34 }: { initials: string; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: brand.jaguar,
        color: brand.paper,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: brand.fDisplay,
        fontWeight: 700,
        fontSize: size * 0.34,
        border: '1px solid ' + brand.gold,
        flexShrink: 0,
      }}
    >
      {initials || '—'}
    </span>
  );
}

/** A simple empty hint inside a surface card. */
export function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: brand.fSans,
        fontWeight: 500,
        fontSize: 13,
        color: brand.sub,
        textAlign: 'center',
        padding: '32px 0',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Page wrapper: a padded shell with the page lede + stat deck, then the children
 * (usually a Refine <List title={false}> with the table, or a custom layout).
 */
export function ListPage({
  intro,
  stats,
  statColumns,
  children,
}: {
  intro?: IntroProps;
  stats?: Stat[];
  statColumns?: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ padding: '22px 26px 28px' }}>
      {intro ? <PageIntro {...intro} /> : null}
      {stats && stats.length ? <StatStrip stats={stats} columns={statColumns} /> : null}
      {children}
    </div>
  );
}

/** Wrap a Refine <List>'s table so it sits in a clean white surface with clipped corners. */
export function TableSurface({ children }: { children: React.ReactNode }) {
  return <div style={{ ...surface, overflow: 'hidden' }}>{children}</div>;
}

/**
 * Make an AntD <Table> row clickable → the record's Show page. Returns the value
 * to spread into `<Table onRow={...}>`.
 *
 *   const onRow = useRowShow('events')
 *   <Table {...tableProps} rowKey="id" onRow={onRow}>…</Table>
 *
 * Clicks that originate INSIDE an interactive control — the row's action buttons
 * (Show/Edit/Delete), a Switch toggle, a link, a checkbox/select, an input — are
 * ignored via a `closest()` guard, so whole-row navigation never fights those
 * controls (no per-button stopPropagation needed). The whole row shows a pointer
 * cursor. Pass a resource whose route has a Show page.
 */
export function useRowShow(resource: string) {
  const { show } = useNavigation();
  return (record: { id: string | number }) => ({
    onClick: (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      if (
        el.closest(
          'button, a, input, label, select, .ant-btn, .ant-switch, .ant-checkbox, .ant-select, .ant-dropdown-trigger, [role="button"], [role="switch"]',
        )
      ) {
        return;
      }
      show(resource, record.id);
    },
    style: { cursor: 'pointer' as const },
  });
}
